import useScopedLoad from './useScopedLoad';
// © 2025 COPYright LAHO – https://www.linkedin.com/in/laurens-holtkamp-7a686b2b/
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useGroup } from '../context/GroupContext';
import { formatMatchLabel } from '../utils/matches';
import { normalizeLegacyGathering, mergeGatheringSources } from '../services/gatheringService';

export { formatMatchLabel } from '../utils/matches';

const EMPTY_MATCHES = [];

const isMissingGatheringsSchema = (error) =>
  ['42P01', 'PGRST200', 'PGRST204', 'PGRST205'].includes(error?.code);

const normalizeGathering = (session) => ({
  ...session,
  // Compatibility fields keep the existing ordering screens unchanged while
  // their source of truth moves from matches to gatherings.
  date: session.starts_at,
  label: session.label,
  opponent: session.title,
  home_team: session.title,
  away_team: '',
  location: session.locations?.name || '',
  locationName: session.locations?.name || '',
  isGathering: true,
});

const MatchesContext = createContext(null);

// One request pair per active app/group, shared by the header, selectors and pages.
// Financial reads are deliberately not cached here.
export const MatchesProvider = ({ children }) => {
  const { group } = useGroup();
  const load = useCallback(async () => {
    const [gatheringResult, legacyResult] = await Promise.all([
      supabase.from('sessions')
        .select('id, group_id, location_id, title, label, starts_at, status, locations(id, name)')
        .eq('group_id', group.id).order('starts_at', { ascending: true }),
      supabase.from('matches').select('*, locations(id, name)')
        .eq('group_id', group.id).order('date', { ascending: true }),
    ]);
    if ((gatheringResult.error && !isMissingGatheringsSchema(gatheringResult.error))
      || (legacyResult.error && !isMissingGatheringsSchema(legacyResult.error))
      || (gatheringResult.error && legacyResult.error)) {
      throw new Error('Bijeenkomsten konden niet worden geladen');
    }
    const gatherings = gatheringResult.error ? [] : (gatheringResult.data || []).map(normalizeGathering);
    const legacy = legacyResult.error ? [] : (legacyResult.data || []).map(normalizeLegacyGathering);
    return {
      all: mergeGatheringSources(gatherings, legacy),
      home: mergeGatheringSources(gatherings, legacy.filter(match => match.location === 'home')),
    };
  }, [group?.id]);
  const { data, loading, error, hasLoaded, refresh } = useScopedLoad(group?.id, load);
  const lastRefresh = useRef(Date.now());
  useEffect(() => {
    const reload = () => { lastRefresh.current = Date.now(); refresh(); };
    const resume = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh.current >= 30000) reload();
    };
    window.addEventListener('bb:gatherings-changed', reload);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.removeEventListener('bb:gatherings-changed', reload);
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [refresh]);
  const value = useMemo(() => ({ data, loading, error, hasLoaded, refresh }),
    [data, loading, error, hasLoaded, refresh]);
  return <MatchesContext.Provider value={value}>{children}</MatchesContext.Provider>;
};

const useMatches = ({ homeOnly = true } = {}) => {
  const state = useContext(MatchesContext);
  if (!state) throw new Error('useMatches requires MatchesProvider');
  const { data, loading, error, hasLoaded, refresh } = state;
  const matches = data?.[homeOnly ? 'home' : 'all'] || EMPTY_MATCHES;
  const errorMsg = error ? 'Bijeenkomsten konden niet worden geladen' : '';

  const closestMatch = useMemo(() => {
    if (!matches.length) return null;
    const upcoming = matches.find((item) => new Date(item.date) >= new Date());
    return upcoming || matches[matches.length - 1];
  }, [matches]);

  const labelForMatchId = (id) => formatMatchLabel(matches.find((item) => item.id === id));

  return {
    matches,
    gatherings: matches,
    loading,
    errorMsg,
    hasLoaded,
    refresh,
    closestMatch,
    closestGathering: closestMatch,
    formatMatchLabel,
    labelForMatchId,
  };
};

export default useMatches;
